import {
  BRIDGE_PROTOCOL,
  BRIDGE_PROTOCOL_VERSION,
  type BridgeCommand,
  type BridgeCommandResults,
  type BridgeFetchResult,
  type BridgeMessage,
  type BridgeReply,
} from "../bridge/protocol.ts";
import {
  NetworkError,
  type HttpRequest,
  type HttpResponse,
  type ImageRequest,
  type PreparedImage,
} from "../types.ts";
import { assertSourceUrl, getSourcePolicy } from "../SourceRegistry.ts";
import { extensionBridge } from "../../bridge/ExtensionBridgeClient.ts";

type Runtime = Pick<typeof chrome.runtime, "sendMessage" | "lastError">;
export interface ExtensionTransportOptions {
  extensionId?: string;
  discoveryTimeoutMs?: number;
  runtime?: Runtime;
}

const isExtensionId = (id: unknown): id is string =>
  typeof id === "string" && /^[a-p]{32}$/.test(id);
const errorCodes = new Set([
  "BRIDGE_UNAVAILABLE",
  "PERMISSION_REQUIRED",
  "LOGIN_REQUIRED",
  "QUOTA_EXHAUSTED",
  "CHALLENGE_REQUIRED",
  "HTTP_ERROR",
  "TIMEOUT",
  "ABORTED",
  "INVALID_RESPONSE",
  "NETWORK_BUSY",
]);

export class ExtensionTransport {
  private availability?: { hint: string; until: number; value: boolean };
  private checking?: Promise<boolean>;
  private readonly registrations = new Map<string, Promise<void>>();
  private legacyRelay = false;
  private registrySupported?: boolean;
  private sourceTabImagesSupported = false;
  private dynamicSupported = false;
  constructor(private readonly options: ExtensionTransportOptions = {}) {}

  private invalidateDiscovery = (): void => {
    this.availability = undefined;
    this.registrySupported = undefined;
    this.sourceTabImagesSupported = false;
    this.dynamicSupported = false;
  };

  async isAvailable(): Promise<boolean> {
    if (typeof document !== "undefined") {
      document.addEventListener("yomiri-bridge-ready", this.invalidateDiscovery, { once: true });
    }
    const hint =
      (typeof document !== "undefined"
        ? document.documentElement?.dataset.yomiriBridgeId
        : undefined) ??
      this.options.extensionId ??
      "";
    // Content scripts can announce after the first route request. Let discovery
    // wait and probe the legacy relay instead of reporting disconnected early.
    if (this.availability?.hint === hint && this.availability.until > Date.now())
      return this.availability.value;
    if (this.checking) return this.checking;
    this.checking = this.ping()
      .then((ping) => ping.capabilities.includes("fetch"))
      .catch(() => false)
      .then((value) => {
        this.availability = { hint, until: Date.now() + (value ? 60000 : 5000), value };
        return value;
      })
      .finally(() => {
        this.checking = undefined;
      });
    return this.checking;
  }

  markUnavailable(): void {
    this.availability = {
      hint: this.availability?.hint ?? "",
      until: Date.now() + 5000,
      value: false,
    };
    this.registrations.clear();
  }

  private async ensureSource(sourceId: string, signal?: AbortSignal): Promise<void> {
    const source = getSourcePolicy(sourceId);
    if (this.registrySupported === undefined) await this.ping();
    if (source.dynamic && !this.dynamicSupported)
      throw new NetworkError(
        "PERMISSION_REQUIRED",
        "Actualizá Yomiri Bridge a 2.3 para instalar fuentes de repositorios.",
      );
    if (!this.registrySupported) {
      if (signal?.aborted) throw new NetworkError("ABORTED", "Solicitud cancelada.");
      return;
    }
    const extensionId = this.options.runtime ? await this.extensionId(signal) : "content-relay";
    const key = `${extensionId}:${source.id}:${source.revision}`;
    let pending = this.registrations.get(key);
    if (!pending) {
      pending = this.send(
        {
          type: "REGISTER_SOURCE",
          sourceId: source.id,
          revision: source.revision,
          allowedHosts: [...source.allowedHosts],
          ...(source.dynamic ? { baseUrl: source.baseUrl } : {}),
        },
        4000,
      )
        .then((reply) => {
          if (reply.sourceId !== source.id || reply.revision !== source.revision)
            throw new NetworkError("INVALID_RESPONSE", "El catálogo de Bridge no coincide.");
        })
        .catch((error) => {
          this.registrations.delete(key);
          throw error;
        });
      this.registrations.set(key, pending);
    }
    await pending;
    if (signal?.aborted) throw new NetworkError("ABORTED", "Solicitud cancelada.");
  }

  async ping(): Promise<BridgeCommandResults["PING"]> {
    let result: BridgeCommandResults["PING"];
    try {
      result = await this.send({ type: "PING" }, 4000);
      this.legacyRelay = false;
    } catch (error) {
      if (this.options.runtime || typeof window === "undefined") throw error;
      const legacy = await extensionBridge.ping();
      if (!legacy.connected) throw error;
      this.legacyRelay = true;
      this.registrySupported = false;
      return { extensionVersion: legacy.version ?? "legacy", capabilities: ["fetch"] };
    }
    if (typeof result.extensionVersion !== "string" || !Array.isArray(result.capabilities))
      throw new NetworkError("INVALID_RESPONSE", "Respuesta de Bridge inválida.");
    this.registrySupported = result.capabilities.includes("source-registry");
    this.sourceTabImagesSupported = result.capabilities.includes("source-tab-images");
    this.dynamicSupported = result.capabilities.includes("dynamic-hosts");
    return result;
  }
  async unregisterSource(sourceId: string): Promise<void> {
    if (!sourceId.startsWith("dynamic-")) return;
    await this.send({ type: "UNREGISTER_SOURCE", sourceId }, 4000);
    for (const key of this.registrations.keys())
      if (key.includes(`:${sourceId}:`)) this.registrations.delete(key);
  }

  async text(request: HttpRequest & { responseType: "text" }): Promise<HttpResponse<string>> {
    const result = await this.fetch(request);
    if (result.body.encoding !== "text")
      throw new NetworkError("INVALID_RESPONSE", "Bridge no devolvió texto.");
    return this.response(result, result.body.value);
  }

  async bytes(request: HttpRequest & { responseType: "bytes" }): Promise<HttpResponse<Uint8Array>> {
    const result = await this.fetch(request);
    if (result.body.encoding !== "base64" || result.body.value.length > 24 * 1024 * 1024)
      throw new NetworkError("INVALID_RESPONSE", "Respuesta binaria inválida.");
    try {
      const decoded = atob(result.body.value);
      return this.response(
        result,
        Uint8Array.from(decoded, (char) => char.charCodeAt(0)),
      );
    } catch {
      throw new NetworkError("INVALID_RESPONSE", "Bridge devolvió Base64 inválido.");
    }
  }

  async prepareImage(request: ImageRequest): Promise<PreparedImage> {
    await this.prepareImages(
      request.sourceId,
      [request.url],
      request.referer ?? `${new URL(request.url).origin}/`,
    );
    // Read authenticated Bokugen images through Bridge instead of a cross-site <img>.
    // The resulting blob stays in the user's browser and consumes no Worker requests.
    if (getSourcePolicy(request.sourceId).id === "bokugen") {
      let response: HttpResponse<Uint8Array>;
      try {
      response = await this.bytes({
        sourceId: request.sourceId,
        url: request.url,
        method: "GET",
        headers: request.headers,
        credentials: "include",
        responseType: "bytes",
        purpose: "image",
        signal: request.signal,
        cache: { ttlMs: 0, scope: "session" },
      });
      if (response.status >= 400)
        throw new NetworkError('HTTP_ERROR', `Imagen: HTTP ${response.status}`, response.status, 'extension');
      } catch (error) {
        if (!this.sourceTabImagesSupported || !(error instanceof NetworkError) ||
          !(error.code === 'CHALLENGE_REQUIRED' || error.status === 403 || error.status === 503)) throw error;
        const result = await this.send({type:'SOURCE_TAB_IMAGE',sourceId:'bokugen',url:request.url,referer:request.referer!},16000);
        if(result.body.encoding!=='base64')throw new NetworkError('INVALID_RESPONSE','Bridge no devolvió una imagen binaria.');
        response=this.response(result,Uint8Array.from(atob(result.body.value),c=>c.charCodeAt(0)));
      }
      const contentType = response.headers["content-type"] ?? "";
      if (response.status >= 400 || !contentType.startsWith("image/"))
        throw new NetworkError("INVALID_RESPONSE", `Bokugen: imagen HTTP ${response.status} (${contentType || "sin tipo"}).`, response.status, "extension");
      const blob = new Blob([response.body.slice().buffer as ArrayBuffer], { type: contentType });
      const url = URL.createObjectURL(blob);
      return { url, delivery: "blob", release: () => URL.revokeObjectURL(url) };
    }
    return { url: request.url, delivery: "direct", release() {} };
  }

  async prepareImages(sourceId: string, urls: string[], referer: string): Promise<void> {
    await this.ensureSource(sourceId);
    for (const url of urls) assertSourceUrl(sourceId, url);
    assertSourceUrl(sourceId, referer);
    if (this.legacyRelay) return; // Older installations retain their previous direct image delivery until reloaded.
    const result = await this.send({ type: "PREPARE_IMAGES", sourceId, urls, referer }, 6000);
    if (!Number.isInteger(result.prepared) || result.prepared < 0)
      throw new NetworkError("INVALID_RESPONSE", "Bridge no confirmó las reglas de imágenes.");
  }

  private response<T extends string | Uint8Array>(
    result: BridgeFetchResult,
    body: T,
  ): HttpResponse<T> {
    return {
      status: result.status,
      finalUrl: result.finalUrl,
      headers: result.headers,
      body,
      classification: result.classification,
      transport: "extension",
      fromCache: false,
    };
  }

  private async fetch(request: HttpRequest): Promise<BridgeFetchResult> {
    assertSourceUrl(request.sourceId, request.url);
    await this.ensureSource(request.sourceId, request.signal);
    if (this.legacyRelay) {
      const legacy = await extensionBridge.fetch(request.url, {
        ...request,
        responseType: request.responseType === "bytes" ? "base64" : "text",
      });
      if (request.signal?.aborted) throw new NetworkError("ABORTED", "Solicitud cancelada.");
      const finalUrl = (legacy as typeof legacy & { finalUrl?: string }).finalUrl ?? request.url;
      assertSourceUrl(request.sourceId, finalUrl);
      const binary = request.responseType === "bytes";
      if (typeof legacy.data !== "string")
        throw new NetworkError("INVALID_RESPONSE", "El relay no devolvió texto.");
      const value = binary ? legacy.data.replace(/^data:[^,]*,/, "") : legacy.data;
      if (
        legacy.headers["cf-mitigated"] === "challenge" ||
        /<title[^>]*>\s*Just a moment|id=["']challenge-form/i.test(value)
      )
        throw new NetworkError(
          "CHALLENGE_REQUIRED",
          "La fuente solicita verificar el navegador.",
          legacy.status,
        );
      return {
        status: legacy.status,
        finalUrl,
        headers: legacy.headers,
        classification: "content",
        body: { encoding: binary ? "base64" : "text", value },
      };
    }
    const {
      signal,
      cache: _cache,
      corsOpen: _cors,
      requiresSession: _session,
      purpose: _purpose,
      ...serializable
    } = request;
    const result = await this.send(
      { type: "FETCH", request: serializable },
      (request.timeoutMs ?? 25000) + 1500,
      signal,
    );
    if (
      !Number.isInteger(result.status) ||
      result.status < 100 ||
      result.status > 599 ||
      typeof result.finalUrl !== "string" ||
      !result.headers ||
      Object.values(result.headers).some((value) => typeof value !== "string") ||
      !result.body ||
      typeof result.body.value !== "string" ||
      !["text", "base64"].includes(result.body.encoding) ||
      !["content", "challenge"].includes(result.classification)
    )
      throw new NetworkError("INVALID_RESPONSE", "Respuesta HTTP de Bridge inválida.");
    if (result.classification === "challenge")
      throw new NetworkError(
        "CHALLENGE_REQUIRED",
        "La fuente solicita verificar el navegador.",
        result.status,
      );
    return result;
  }

  private runtime(): Runtime {
    const runtime = this.options.runtime ??
      (typeof window === "undefined" && typeof chrome !== "undefined" ? chrome.runtime : undefined);
    if (!runtime?.sendMessage) {
      if (typeof window === "undefined")
        throw new NetworkError("BRIDGE_UNAVAILABLE", "Yomiri Bridge no está disponible.");
      // Isolated content scripts have runtime access even when the page world does not.
      return {
        sendMessage: ((_id: string, message: BridgeMessage, callback: (reply: unknown) => void) => {
          const listen = (event: MessageEvent) => {
            if (
              event.source !== window ||
              event.origin !== location.origin ||
              event.data?.source !== "yomiri-extension" ||
              event.data.requestId !== message.requestId
            )
              return;
            clearTimeout(timer);
            window.removeEventListener("message", listen);
            callback(event.data.response);
          };
          const timer = setTimeout(() => {
            window.removeEventListener("message", listen);
            callback(undefined);
          }, 30000);
          window.addEventListener("message", listen);
          window.postMessage(
            {
              source: "yomiri-web",
              requestId: message.requestId,
              action: "YOMIRI_NETWORK",
              payload: message,
            },
            location.origin,
          );
        }) as Runtime["sendMessage"],
        lastError: undefined,
      };
    }
    return runtime;
  }

  private async extensionId(signal?: AbortSignal): Promise<string> {
    if (signal?.aborted) throw new NetworkError("ABORTED", "Solicitud cancelada.");
    const read = () =>
      typeof document !== "undefined"
        ? document.documentElement?.dataset.yomiriBridgeId
        : undefined;
    const discovered = read();
    if (isExtensionId(discovered)) return discovered;
    if (isExtensionId(this.options.extensionId)) return this.options.extensionId;
    if (typeof document === "undefined")
      throw new NetworkError("BRIDGE_UNAVAILABLE", "Yomiri Bridge no está disponible.");
    return new Promise<string>((resolve, reject) => {
      const cleanup = () => {
        clearTimeout(timer);
        observer.disconnect();
        document.removeEventListener("yomiri-bridge-ready", check);
        signal?.removeEventListener("abort", abort);
      };
      const check = () => {
        const id = read();
        if (isExtensionId(id)) {
          cleanup();
          resolve(id);
        }
      };
      const abort = () => {
        cleanup();
        reject(new NetworkError("ABORTED", "Solicitud cancelada."));
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(
          new NetworkError("BRIDGE_UNAVAILABLE", "Instalá Yomiri Bridge y recargá esta página."),
        );
      }, this.options.discoveryTimeoutMs ?? 1500);
      const observer = new MutationObserver(check);
      observer.observe(document, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["data-yomiri-bridge-id"],
      });
      document.addEventListener("yomiri-bridge-ready", check);
      signal?.addEventListener("abort", abort, { once: true });
      if (signal?.aborted) abort();
      else check();
    });
  }

  private async send<K extends BridgeCommand["type"]>(
    command: Extract<BridgeCommand, { type: K }>,
    timeoutMs: number,
    signal?: AbortSignal,
  ): Promise<BridgeCommandResults[K]> {
    const runtime = this.runtime();
    // The isolated-world relay already knows its own extension ID. Requiring
    // a DOM discovery marker here prevented even PING when that marker was absent.
    const extensionId = this.options.runtime ? await this.extensionId(signal) : "content-relay";
    const requestId = crypto.randomUUID();
    const message: BridgeMessage = {
      protocol: BRIDGE_PROTOCOL,
      version: BRIDGE_PROTOCOL_VERSION,
      requestId,
      command,
    };
    return new Promise((resolve, reject) => {
      let settled = false;
      let sent = false;
      const cancel = () => {
        if (!sent || command.type !== "FETCH") return;
        try {
          runtime.sendMessage(
            extensionId,
            {
              protocol: BRIDGE_PROTOCOL,
              version: BRIDGE_PROTOCOL_VERSION,
              requestId: crypto.randomUUID(),
              command: { type: "CANCEL", targetRequestId: requestId },
            } satisfies BridgeMessage,
            () => {
              void runtime.lastError;
            },
          );
        } catch {
          /* Original cancellation/timeout remains the user-visible error. */
        }
      };
      const finish = (error?: Error, data?: BridgeCommandResults[K]) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        signal?.removeEventListener("abort", abort);
        if (error) reject(error);
        else resolve(data!);
      };
      const abort = () => {
        cancel();
        finish(new NetworkError("ABORTED", "Solicitud cancelada."));
      };
      const timer = setTimeout(() => {
        cancel();
        finish(new NetworkError("TIMEOUT", "Yomiri Bridge tardó demasiado en responder."));
      }, timeoutMs);
      signal?.addEventListener("abort", abort, { once: true });
      if (signal?.aborted) {
        abort();
        return;
      }
      try {
        sent = true;
        runtime.sendMessage(extensionId, message, (raw: unknown) => {
          const lastError = runtime.lastError;
          if (settled) return;
          if (lastError) {
            finish(
              new NetworkError("BRIDGE_UNAVAILABLE", lastError.message ?? "Bridge desconectado."),
            );
            return;
          }
          const reply = raw as BridgeReply<K> | undefined;
          if (
            !reply ||
            reply.protocol !== BRIDGE_PROTOCOL ||
            reply.version !== BRIDGE_PROTOCOL_VERSION ||
            reply.requestId !== requestId ||
            typeof reply.ok !== "boolean"
          ) {
            finish(
              new NetworkError(
                "INVALID_RESPONSE",
                "Actualizá Yomiri Bridge para usar este transporte.",
              ),
            );
            return;
          }
          if (reply.ok === false) {
            const error = reply.error;
            finish(
              new NetworkError(
                errorCodes.has(error?.code) ? error.code : "INVALID_RESPONSE",
                typeof error?.message === "string"
                  ? error.message
                  : "Respuesta de Bridge inválida.",
                error?.status,
              ),
            );
          } else if (!reply.data || typeof reply.data !== "object")
            finish(new NetworkError("INVALID_RESPONSE", "Respuesta de Bridge inválida."));
          else finish(undefined, reply.data);
        });
      } catch {
        finish(new NetworkError("BRIDGE_UNAVAILABLE", "No se pudo comunicar con Yomiri Bridge."));
      }
    });
  }
}
